// ============================================================================
// LE CALENDRIER DES ENVOIS — le fuseau du magasin et ses jours d'ouverture.
//
// ⚠️ UNE SECTION DE LA CARTE « Horaires & journée fiscale », AVEC SON PROPRE ENREGISTREMENT (décision utilisateur
//    2026-10-03 : « une carte, deux enregistrements »). Le dessin réunit jours, fuseau et horaires dans une carte ; mais
//    leurs conséquences diffèrent — les heures déplacent la journée fiscale, ceux-ci l'heure des envois —, donc chaque
//    partie garde son bouton et son message, et l'échec de l'une ne bloque pas l'autre.
// ⚠️ 🔴 IL VIT ICI, À CÔTÉ DES HORAIRES, ET PAS DANS LE CATALOGUE DES PARAMÈTRES. Un réglage se règle
// à UN SEUL endroit, et c'est celui qui a le CONTEXTE : on répond à « quand ce magasin est-il
// ouvert ? » une fois, pas deux. Une case nue dans un catalogue générique ne saurait ni que le fuseau
// commande l'heure d'un envoi, ni que les jours commandent son rattrapage.
//
// ⚠️ 🔴 MAIS CE N'EST PAS LE MÊME FAIT QUE LES HORAIRES, ET L'ÉCRAN LE DIT. Les HEURES commandent la
// journée FISCALE — la bascule NF525 de toute la flotte du magasin. Les JOURS commandent le
// RATTRAPAGE d'un envoi manqué. Posés côte à côte sans un mot, on conclurait que fermer le lundi
// empêche de vendre le lundi : c'est faux, et ça le resterait longtemps sans que rien ne le signale.
//
// ⚠️ SANS FUSEAU, AUCUNE CAMPAGNE NE PART de ce magasin (décision utilisateur 2026-09) : le repli
// `Europe/Paris` empêche un magasin non réglé d'être bloqué partout ailleurs, mais pour un ENVOI il ne
// protège de rien — le message partirait à l'heure d'un autre pays, l'envoi RÉUSSIRAIT, et personne ne
// l'apprendrait avant qu'un client se plaigne. L'écran doit donc dire ce que le vide coûte, pas
// seulement que le champ est vide.
// ============================================================================
import { useEffect, useState, type CSSProperties } from 'react';
import { Picker, showToast, Trans } from '@orgazone/ui-web';
import type { StoreCalendar } from '../api';
import { currentLang } from '../lang';
import { t } from '../i18n';

/**
 * LES JOURS, EN TOUTES LETTRES — un exploitant ne coche pas des numéros.
 *
 * 🔴 C'ÉTAIT UNE TABLE FRANÇAISE, la SIXIÈME du dépôt, et l'ironie tient en deux lignes : le
 *    commentaire juste en dessous dit déjà « ON DEMANDE À `Intl`, ON NE TIENT PAS DE LISTE » — pour
 *    les fuseaux. Une langue de plus aurait redemandé sept clés.
 *
 * ⚠️ L'ORDRE NE SE DÉRIVE PAS, LUI : il vient de la DONNÉE (`1 = lundi … 7 = dimanche`, la
 *    convention ISO du réglage `store.open_days`). `Intl` sait nommer un jour, il ne décide pas où
 *    commence la semaine — en anglais américain elle commence dimanche. On garde donc l'ordre ISO
 *    et on ne dérive QUE les noms ; l'index `k + 1` reste la clé du réglage.
 * ⚠️ APPELÉ AU RENDU, jamais au chargement : la langue du compte n’arrive qu’après. Et la capitale
 *    est ajoutée, `Intl` rendant « lundi » et « Monday ».
 */
function joursDeLaSemaine(width: 'long' | 'short' = 'long'): string[] {
  const f = new Intl.DateTimeFormat(currentLang(), { weekday: width, timeZone: 'UTC' });
  // Le 1ᵉʳ janvier 2024 était un LUNDI : la référence est une DATE, jamais un indice écrit à la main.
  return Array.from({ length: 7 }, (_, i) => {
    // La forme courte porte un point en français (« lun. ») : le bouton du dessin dit « Lun ».
    const nom = f.format(new Date(Date.UTC(2024, 0, 1 + i))).replace(/\.$/, '');
    return nom.charAt(0).toUpperCase() + nom.slice(1);
  });
}

/**
 * LES FUSEAUX PROPOSÉS, calculés UNE FOIS au chargement du module.
 *
 * ⚠️ ON DEMANDE À `Intl`, ON NE TIENT PAS DE LISTE : les fuseaux changent plusieurs fois par an, et
 * une liste recopiée vieillirait en silence — elle refuserait alors un fuseau parfaitement valide.
 *
 * ⚠️ HORS DU COMPOSANT, et ce n'est pas un détail de style : recalculée à chaque rendu, cette liste de
 * plusieurs centaines d’entrées serait un tableau NEUF à chaque frappe dans la recherche — le picker
 * le prendrait pour un changement d’options et refermerait son panneau sous les doigts.
 *
 * ⚠️ SI `Intl` NE RÉPOND PAS, LA LISTE EST VIDE ET L'ÉCRAN LE DIT — jamais une liste inventée : un
 * choix parmi trois fuseaux plausibles serait pire que pas de choix du tout.
 */
const FUSEAUX: { value: string; label: string }[] = (() => {
  try {
    const f = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf;
    const zones = typeof f === 'function' ? f('timeZone') : [];
    return zones.map((z) => ({ value: z, label: z }));
  } catch { return []; }
})();

const row: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 14, padding: '13px 16px', borderBottom: '1px solid var(--ink-100)', flexWrap: 'wrap',
};
const rowLabel: CSSProperties = { display: 'block', fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-900)' };
const rowSub: CSSProperties = { display: 'block', fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginTop: 2, lineHeight: 1.45 };
const band: CSSProperties = {
  padding: '9px 16px', borderBottom: '1px solid var(--ink-100)', fontSize: 'var(--t-note)', lineHeight: 1.5,
  background: 'var(--wa-bg)', color: 'var(--wa-fg)',
};

/**
 * LA SECTION « CALENDRIER DES ENVOIS » de la carte des horaires — jours d'ouverture et fuseau, et SON enregistrement.
 * ⚠️ Une lecture ratée se DIT : on ne propose aucune saisie sur un état inconnu, qui écraserait ce qui est déclaré.
 */
export function CalendarSection({ calendrier, onEnregistrer }: {
  calendrier: StoreCalendar | null;
  onEnregistrer: (timezone: string, openDays: number[]) => Promise<StoreCalendar>;
}) {
  const [tz, setTz] = useState('');
  const [jours, setJours] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);

  // Les champs suivent le serveur — sinon la réponse d'un enregistrement écraserait une saisie en
  // cours, et l'exploitant verrait ses choix changer sous ses doigts.
  useEffect(() => {
    if (!calendrier) return;
    // ⚠️ UN FUSEAU NON POSÉ LAISSE LE CHAMP VIDE, jamais `Europe/Paris` : afficher le repli le ferait
    //    passer pour un choix, et personne ne corrigerait un réglage qui a l'air déjà fait.
    setTz(calendrier.timezoneSet ? calendrier.timezone : '');
    setJours(calendrier.openDaysSet ? calendrier.openDays : [1, 2, 3, 4, 5, 6, 7]);
  }, [calendrier]);

  const modifie = calendrier !== null
    && (tz !== (calendrier.timezoneSet ? calendrier.timezone : '')
      || jours.join(',') !== (calendrier.openDaysSet ? calendrier.openDays : [1, 2, 3, 4, 5, 6, 7]).join(','));
  const valide = tz.trim() !== '' && jours.length > 0;

  const basculer = (j: number) => setJours((x) => (
    x.includes(j) ? x.filter((y) => y !== j) : [...x, j].sort((a, b) => a - b)));

  const valider = async () => {
    if (!modifie || !valide || busy) return;
    setBusy(true);
    try {
      await onEnregistrer(tz.trim(), jours);
      showToast(t('Calendrier des envois enregistré.'));
    } catch (e) { showToast((e as Error).message, 'err', 9000); }
    finally { setBusy(false); }
  };

  if (calendrier === null) {
    return (
      <div style={band}>
        {t('Le calendrier des envois ne se lit pas — mieux vaut ne rien saisir tant qu’on ne sait pas ce qui est déclaré.')}
      </div>
    );
  }

  return (
    <>
      {/* ⚠️ 🔴 LA PHRASE QUI ÉVITE LA CONFUSION AVEC LES HORAIRES, et elle est au-dessus des champs :
          lue après, elle arriverait trop tard pour empêcher le contresens. */}
      <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--ink-100)', background: 'var(--ink-50)', fontSize: 'var(--t-note)', color: 'var(--ink-600)', lineHeight: 1.5 }}>
        <Trans k='Jours et fuseau commandent <1>l’heure de vos envois</1> — sans rapport avec la journée fiscale, que commandent les horaires ci-dessous.' parts={[(x) => <strong>{x}</strong>]} />
      </div>

      {/* ⚠️ L'AVERTISSEMENT NE S'AFFICHE QUE QUAND IL EST VRAI — un bandeau permanent finit par ne plus
          être lu, et celui-ci porte une conséquence réelle : plus aucune campagne ne peut partir. */}
      {/* ⚠️ UN NAVIGATEUR QUI NE SAIT PAS LISTER LES FUSEAUX LE DIT, plutôt que d’offrir un choix vide
          où l’exploitant chercherait sans comprendre. Le cas est rare (navigateur ancien), et le
          taire ferait passer une limite du navigateur pour une panne du back-office. */}
      {FUSEAUX.length === 0 && (
        <div style={band}>
          {t('Ce navigateur ne sait pas lister les fuseaux horaires. Ouvrez cette page depuis un navigateur récent pour choisir le vôtre.')}
        </div>
      )}

      {!calendrier.timezoneSet && (
        <div style={band}>
          <Trans k='<1>Aucune campagne ne peut partir de ce magasin</1> tant que le fuseau n’est pas déclaré : un message partirait à l’heure d’un autre pays sans que rien ne le signale.' parts={[(x) => <strong>{x}</strong>]} /></div>
      )}

      {/* L'ORDRE DU DESSIN : les jours d'abord, puis le fuseau. */}
      <div style={row}>
        <span style={{ flex: 1, minWidth: 210 }}>
          <span style={rowLabel}>{t('Jours d’ouverture')}</span>
          {/* ⚠️ « JOUR OUVRÉ » NE VEUT PAS DIRE LUNDI-VENDREDI POUR UN COMMERCE : le samedi est le meilleur
              jour d'un SMS de soldes, et beaucoup de magasins ferment le lundi. */}
          <span style={rowSub}>
            {jours.length === 0
              ? t('Aucun jour coché : un envoi manqué n’aurait aucun jour où repartir.')
              : t('Si un envoi ne peut pas partir à l’heure prévue, il repart au prochain jour coché — jamais un jour de fermeture.')}
          </span>
        </span>
        <span style={{ display: 'inline-flex', gap: 5, flexWrap: 'wrap' }}>
          {joursDeLaSemaine('short').map((l, k) => {
            const j = k + 1;
            const on = jours.includes(j);
            return (
              <button
                key={j} type="button" aria-pressed={on} onClick={() => basculer(j)}
                aria-label={joursDeLaSemaine()[k]}
                style={{
                  width: 40, height: 32, borderRadius: 'var(--r-ctrl)', cursor: 'pointer',
                  fontSize: 'var(--t-dense)', fontWeight: 700,
                  border: `1.5px solid ${on ? 'var(--teal-400)' : 'var(--ink-200)'}`,
                  background: on ? 'var(--teal-100)' : 'var(--paper)',
                  color: on ? 'var(--teal-800)' : 'var(--ink-400)',
                }}
              >{l}</button>
            );
          })}
        </span>
      </div>

      {/* ⚠️ 🔴 LE PICKER DE LA CHARTE, PAS UN CHAMP AVEC `datalist`. Un champ de saisie natif avec liste de suggestions se rend
          différemment dans chaque navigateur, n’a ni le clavier, ni la restitution du focus, ni le
          panneau de la charte — et il ACCEPTE une saisie libre, donc un fuseau inventé que seul le
          serveur refuserait. Mono-sélection, recherche, AUCUNE création : on ne crée pas un fuseau
          horaire, on en choisit un parmi ceux qui existent. */}
      <div style={row}>
        <span style={{ flex: 1, minWidth: 210 }}>
          <span style={rowLabel}>{t('Fuseau horaire')}</span>
          <span style={rowSub}>
            <Trans k='Il fait qu’un envoi programmé à 9 h part à 9 h <1>chez vous</1> — le serveur, lui, est en UTC.' parts={[(x) => <strong>{x}</strong>]} />
          </span>
        </span>
        <span style={{ minWidth: 220 }}>
          <Picker
            id="bo-mag-fuseau" variant="field"
            label={t('Fuseau horaire')}
            searchPlaceholder={t('Chercher un fuseau… (Paris, Reunion, Martinique…)')}
            allLabel={t('Aucun — à choisir')}
            clearLabel={t('Aucun — à choisir')}
            options={FUSEAUX}
            value={tz}
            onChange={(v) => setTz(v)}
          />
        </span>
      </div>

      {/* ⚠️ SON PROPRE GESTE, AU BAS DE SA SECTION : les horaires, en dessous, ont le leur — et leurs conséquences n'ont
          rien de commun (l'heure des envois ici, la journée fiscale là). */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-100)', flexWrap: 'wrap' }}>
        <span style={{ flex: 1, minWidth: 140, fontSize: 'var(--t-meta)', color: 'var(--ink-500)', lineHeight: 1.5 }}>
          {!modifie ? t('Aucune modification à enregistrer.')
            : !valide ? t('Choisissez un fuseau et au moins un jour.')
              : t('Enregistrer changera l’heure et le rattrapage des envois — pas la journée fiscale.')}
        </span>
        <button
          type="button" onClick={() => { void valider(); }} disabled={!modifie || !valide || busy}
          style={{
            height: 36, padding: '0 14px', border: 'none', borderRadius: 'var(--r-ctrl)',
            background: 'var(--teal-500)', color: '#fff', fontSize: 'var(--t-body)', fontWeight: 600,
            whiteSpace: 'nowrap', cursor: !modifie || !valide || busy ? 'not-allowed' : 'pointer',
            opacity: !modifie || !valide || busy ? 0.5 : 1,
          }}
        >{busy ? t('Enregistrement…') : t('Enregistrer le calendrier')}</button>
      </div>
    </>
  );
}
